import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { bizApi, saveBiz, loadBiz } from "@/lib/bizApi";
import { MusicNotesSimple, Storefront, ArrowLeft, Eye, EyeSlash, CheckCircle } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NegociosRegister() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [plan, setPlan] = useState(params.get("plan") || "basic");
  const [billing, setBilling] = useState("monthly");
  const [locations, setLocations] = useState(1);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (loadBiz()) nav("/negocios/panel"); }, [nav]);
  useEffect(() => {
    axios.get(`${API}/business/plans`).then(r => setPlans(r.data?.plans || [])).catch(() => {});
  }, []);

  const currentPlan = plans.find(p => p.key === plan);
  const computePrice = () => {
    if (!currentPlan) return null;
    let unit = currentPlan.price_monthly;
    if (plan === "premium" && locations >= (currentPlan.volume_threshold || 10)) {
      unit = currentPlan.volume_price;
    }
    let total = unit * locations;
    if (billing === "yearly") total = total * 12 * 0.80;
    return { unit, total: Math.round(total * 100) / 100 };
  };
  const price = computePrice();

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !address.trim() || !phone || !password) return toast.error("Llena todos los campos");
    if (address.trim().length < 8) return toast.error("Escribe el domicilio completo (calle, número, colonia, ciudad)");
    if (password.length < 6) return toast.error("La contraseña debe tener al menos 6 caracteres");
    if (!acceptTerms) return toast.error("Debes aceptar los términos para comercios");
    setLoading(true);
    try {
      const { data } = await bizApi.post("/business/register", {
        name, address, phone, password,
        plan, billing, locations: Number(locations) || 1,
      });
      saveBiz(data.token, data.business);
      toast.success("¡Registro exitoso! Continúa con tu pago.");
      nav("/negocios/panel");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al registrarse");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      <div className="max-w-3xl w-full mx-auto px-4 py-10 flex-1">
        <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6 w-fit px-3 py-2 -ml-3 rounded-full hover:bg-white/5" data-testid="biz-register-back-home">
          <ArrowLeft size={16} weight="bold" /> Volver al inicio
        </Link>
        <Link to="/" className="flex items-center gap-2 mb-8" data-testid="biz-register-brand">
          <div className="w-10 h-10 rounded-full bg-[#FFB800] flex items-center justify-center">
            <MusicNotesSimple weight="fill" size={22} className="text-black" />
          </div>
          <span className="font-display font-bold text-2xl">Tu MusiKa <span className="text-[#B4F34A]">Negocios</span></span>
        </Link>

        <div className="flex items-center gap-2 mb-2">
          <Storefront weight="fill" size={26} className="text-[#B4F34A]" />
          <h1 className="font-display text-3xl font-bold">Registra tu negocio</h1>
        </div>
        <p className="text-white/50 mb-8 leading-relaxed">
          Música original sin derechos de autor. Elige tu plan, paga y comienza a reproducir hoy mismo.
        </p>

        {/* Plan picker */}
        <p className="text-xs uppercase tracking-widest text-white/60 mb-3">1. Elige tu plan</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
          {plans.map(p => {
            const on = plan === p.key;
            return (
              <button
                type="button"
                key={p.key}
                onClick={() => setPlan(p.key)}
                className={`text-left rounded-2xl p-4 border transition-all active:scale-[0.99] ${on ? "border-[#B4F34A] bg-[#B4F34A]/5" : "border-white/10 bg-[#121212] hover:border-white/30"}`}
                data-testid={`select-plan-${p.key}`}
              >
                <p className="font-display font-bold">{p.name.replace("Plan ", "")}</p>
                <p className="text-[10px] uppercase tracking-widest text-white/40 mt-0.5">{p.tagline}</p>
                <p className="font-display font-black text-2xl text-[#FFB800] mt-2">${p.price_monthly}<span className="text-xs text-white/50"> /mes</span></p>
                {on && <CheckCircle weight="fill" size={18} className="text-[#B4F34A] mt-2" />}
              </button>
            );
          })}
        </div>

        {/* Billing + locations */}
        <p className="text-xs uppercase tracking-widest text-white/60 mb-3">2. Ciclo de facturación</p>
        <div className="grid grid-cols-2 gap-3 mb-6">
          <button
            type="button"
            onClick={() => setBilling("monthly")}
            className={`py-3 rounded-xl border transition-all ${billing === "monthly" ? "border-[#FFB800] bg-[#FFB800]/10 text-[#FFB800]" : "border-white/10 bg-[#121212] text-white/70"}`}
            data-testid="billing-monthly"
          >
            Mensual
          </button>
          <button
            type="button"
            onClick={() => setBilling("yearly")}
            className={`py-3 rounded-xl border transition-all relative ${billing === "yearly" ? "border-[#B4F34A] bg-[#B4F34A]/10 text-[#B4F34A]" : "border-white/10 bg-[#121212] text-white/70"}`}
            data-testid="billing-yearly"
          >
            Anual <span className="text-[10px] ml-1 px-1.5 py-0.5 rounded-full bg-[#B4F34A]/20">-20%</span>
          </button>
        </div>

        {plan === "premium" && (
          <>
            <p className="text-xs uppercase tracking-widest text-white/60 mb-2">Número de sucursales</p>
            <input
              type="number"
              min="1"
              value={locations}
              onChange={(e) => setLocations(Math.max(1, Number(e.target.value) || 1))}
              className="input mb-2"
              data-testid="locations-input"
            />
            <p className="text-xs text-white/50 mb-6">
              {locations >= 10 ? <span className="text-[#B4F34A]">¡Descuento por volumen activo! $499 MXN por sucursal.</span> : <span>10+ sucursales: $499 MXN por sucursal.</span>}
            </p>
          </>
        )}

        {/* Price summary */}
        {price && (
          <div className="rounded-2xl bg-[#0a0a0a] border border-white/10 p-4 mb-8" data-testid="price-summary">
            <div className="flex items-center justify-between text-sm">
              <span className="text-white/60">Total a pagar {billing === "yearly" ? "(anual)" : "(mensual)"}</span>
              <span className="font-display font-black text-2xl text-[#B4F34A]" data-testid="price-total">${price.total.toLocaleString("es-MX")} MXN</span>
            </div>
            {billing === "yearly" && <p className="text-xs text-white/40 mt-1">Equivalente a ${Math.round(price.total / 12).toLocaleString("es-MX")} MXN/mes con 20% de descuento.</p>}
          </div>
        )}

        {/* Business info */}
        <p className="text-xs uppercase tracking-widest text-white/60 mb-3">3. Datos del negocio</p>
        <form onSubmit={submit} className="space-y-4" data-testid="biz-register-form">
          <Field label="Nombre del negocio">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej: Café Mante, Bar La Cabrona" className="input" data-testid="biz-name" />
          </Field>
          <Field label="Domicilio completo">
            <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Calle, número, colonia, ciudad, estado" className="input" data-testid="biz-address" />
          </Field>
          <Field label="Teléfono de contacto">
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="55 1234 5678" className="input" data-testid="biz-phone" />
          </Field>
          <Field label="Contraseña">
            <div className="relative">
              <input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mínimo 6 caracteres" className="input pr-12" data-testid="biz-password" />
              <button type="button" onClick={() => setShowPassword(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-white/50 hover:text-white">
                {showPassword ? <EyeSlash size={18} weight="bold" /> : <Eye size={18} weight="bold" />}
              </button>
            </div>
          </Field>
          <label className="flex items-start gap-3 px-4 py-3 rounded-xl bg-white/5 border border-white/10 cursor-pointer">
            <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} className="mt-1 w-4 h-4 accent-[#B4F34A]" data-testid="biz-accept-terms" />
            <span className="text-xs text-white/70 leading-relaxed">
              Acepto los <Link to="/negocios/terminos" target="_blank" className="text-[#B4F34A] underline">Términos del Servicio para Comercios</Link> y el <Link to="/aviso-de-privacidad" target="_blank" className="text-[#B4F34A] underline">Aviso de Privacidad</Link>.
            </span>
          </label>
          <button type="submit" disabled={loading} className="w-full py-4 rounded-full bg-[#B4F34A] hover:bg-[#A0E03A] disabled:opacity-40 text-black font-semibold active:scale-95 transition-all" data-testid="biz-register-submit">
            {loading ? "Registrando…" : "Crear cuenta y continuar al pago"}
          </button>
        </form>

        <p className="text-center text-sm text-white/50 mt-6">
          ¿Ya estás registrado?{" "}
          <Link to="/negocios/login" className="text-[#B4F34A] hover:underline" data-testid="biz-go-login">Inicia sesión</Link>
        </p>
        <p className="text-center text-xs text-white/40 mt-4">
          <Link to="/negocios/info" className="hover:text-white/70 underline" data-testid="biz-go-info">¿Cómo funciona?</Link>
        </p>
      </div>
      <style>{`.input{width:100%;padding:.75rem 1rem;border-radius:.75rem;background:#121212;border:1px solid rgba(255,255,255,.1);color:#fff;outline:none;transition:border-color .2s}.input:focus{border-color:#B4F34A}.input::placeholder{color:rgba(255,255,255,.3)}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">{label}</label>
      {children}
    </div>
  );
}
